import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, GraduationCap, Clock3, Truck, BarChart3, Coffee, Utensils, Snowflake, Sparkles, Heart, Users, Wrench } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { BRAND } from '@/lib/brand';

const CAMPUS_IMG = 'https://customer-assets.emergentagent.com/job_vegan-danish-bakery/artifacts/emfnb4mm_macNcheese.png';
const SANDWICH_IMG = 'https://customer-assets.emergentagent.com/job_vegan-danish-bakery/artifacts/bc9ibtci_VeganBreakfastSandwichBacon2.png';
const CROISSANT_IMG = 'https://customer-assets.emergentagent.com/job_vegan-danish-bakery/artifacts/06nmlr1f_croissants2.png';

const PILLARS = [
  { Icon: Snowflake, title: 'Frozen-to-oven, always', body: 'Every SKU ships blast-frozen and bakes from -23°C in 18–22 minutes at 165°C convection. Zero proofing, zero scratch labor.' },
  { Icon: Truck, title: 'Nationwide LTL delivery', body: 'Weekly refrigerated dock handoff to every Canadian province. Pallet program starts at 108 cases mix-and-match.' },
  { Icon: BarChart3, title: 'Velocity reporting', body: 'POS-integrated dashboards. Restock by SKU. Blended margin tracking on request.' },
  { Icon: GraduationCap, title: 'Student-tested', body: 'Every recipe piloted at real campus micro-markets and dining halls before it enters the SKU list.' },
  { Icon: Sparkles, title: 'Merchandising kit included', body: 'Bake-off cards, shelf-talkers, menu photography and case-facing signage — packaged with every new account.' },
  { Icon: Heart, title: '100% plant-based, always', body: 'Certified vegan across the entire product line. Zero dairy, zero eggs, zero honey — no compromise SKUs.' }
];

const HOW_IT_WORKS = [
  { n: '01', t: 'Discovery call', d: 'Twenty minutes with our campus team — we walk through your daypart, footprint, and current pain points.' },
  { n: '02', t: 'Sample box', d: 'A free 8-SKU tasting box ships to your operator within a week. Kitchen team feedback drives selection.' },
  { n: '03', t: 'Pilot launch', d: 'Start with 1–2 micro-markets or grab-and-go cases. Full merchandising kit and training deck included.' },
  { n: '04', t: 'Scale & report', d: 'Roll out across your full footprint with POS-integrated velocity dashboards and quarterly business reviews.' }
];

const ORG_TYPES = [
  { Icon: GraduationCap, label: 'Colleges & Universities' },
  { Icon: Utensils, label: 'K-12 Dining Programs' },
  { Icon: Coffee, label: 'Campus Cafés & Retail' },
  { Icon: Users, label: 'YMCAs & Community Hubs' }
];

export default function CampusProgram() {
  return (
    <main className="min-h-screen bg-background">
      <Header variant="solid" />

      {/* HERO */}
      <section className="relative pt-28 pb-16 md:pt-36 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-grain opacity-60 pointer-events-none" />
        <div className="absolute -top-32 -left-32 h-[28rem] w-[28rem] rounded-full bg-leaf-soft/40 blur-3xl pointer-events-none" />
        <div className="absolute top-20 -right-32 h-[26rem] w-[26rem] rounded-full bg-honey-soft/50 blur-3xl pointer-events-none" />

        <div className="container mx-auto relative">
          <div className="text-sm text-muted-foreground flex items-center gap-2">
            <Link to="/" className="hover:text-primary">Home</Link>
            <ChevronRight className="h-3.5 w-3.5" />
            <span className="text-primary font-medium">Campus Program</span>
          </div>

          <div className="grid lg:grid-cols-12 gap-10 mt-8 items-center">
            <div className="lg:col-span-7">
              <div className="font-script text-2xl text-leaf">turnkey grab-and-go</div>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-primary font-medium leading-[1.02] mt-2 text-balance">
                We staff the proofer.<br/>
                <span className="italic font-light text-leaf">You staff the morning rush.</span>
              </h1>
              <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-2xl text-pretty">
                A complete plant-based grab-and-go program for Canadian campus dining, K-12 boards,
                YMCAs and community centres — bakery-quality pastries and meals that arrive frozen,
                bake in 18 minutes, and merchandise beautifully every single morning.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button asChild size="lg" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 px-7 h-12">
                  <Link to="/wholesale#request">Request campus pricing</Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="rounded-full border-primary/25 text-primary hover:bg-secondary px-6 h-12">
                  <Link to="/products">See the 8 SKU lines</Link>
                </Button>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="relative">
                <div className="rounded-[2rem] overflow-hidden shadow-pop aspect-[4/5]">
                  <img src={CAMPUS_IMG} alt="Vegan Danish bakery interior" className="w-full h-full object-cover" />
                </div>
                <div className="absolute -right-4 -bottom-6 sm:right-6 sm:-bottom-8 bg-card border border-border rounded-2xl shadow-pop p-5 w-[230px]">
                  <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground">avg. service time</div>
                  <div className="font-display text-4xl text-primary font-medium mt-1">42<span className="text-base text-leaf">s</span></div>
                  <div className="text-[11px] text-muted-foreground mt-2">From freezer to wrapped, hot in student's hand</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SIX PILLARS */}
      <section className="py-16 md:py-24 bg-parchment/40">
        <div className="container mx-auto">
          <div className="max-w-2xl">
            <div className="font-script text-2xl text-leaf">the program</div>
            <h2 className="font-display text-4xl sm:text-5xl text-primary font-medium leading-tight mt-1">
              Six pillars, one<br/>
              <span className="italic font-light text-leaf">morning rush solved.</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
            {PILLARS.map(({ Icon, title, body }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="relative rounded-3xl bg-card border border-border/70 p-7 shadow-soft hover:shadow-card hover:-translate-y-1 transition-all duration-500"
              >
                <div className="h-12 w-12 rounded-2xl gradient-leaf grid place-items-center text-primary-foreground shadow-card">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="font-display text-xl font-semibold text-primary mt-5">{title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mt-2">{body}</p>
                <div className="mt-5 text-xs uppercase tracking-[0.18em] text-leaf font-medium">0{i + 1} / 06</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="font-script text-2xl text-leaf">how it works</div>
            <h2 className="font-display text-3xl sm:text-4xl text-primary font-medium leading-tight mt-1">
              From first call<br/><span className="italic font-light text-leaf">to full rollout in ~30 days.</span>
            </h2>
            <p className="text-muted-foreground mt-5 max-w-md">
              A predictable, low-risk onboarding path built around your operator's calendar.
              No commitment until after the tasting.
            </p>
            <div className="mt-8 rounded-2xl overflow-hidden shadow-card aspect-[4/3]">
              <img src={SANDWICH_IMG} alt="Plant-based breakfast sandwich" className="w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="grid sm:grid-cols-2 gap-4">
              {HOW_IT_WORKS.map((s) => (
                <motion.div
                  key={s.n}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                  className="p-6 rounded-2xl bg-card border border-border/70 shadow-soft"
                >
                  <div className="font-display text-3xl text-leaf font-medium">{s.n}</div>
                  <div className="font-display text-lg font-semibold text-primary mt-1">{s.t}</div>
                  <div className="text-sm text-muted-foreground mt-2 leading-relaxed">{s.d}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="py-16 md:py-20 bg-parchment/40">
        <div className="container mx-auto">
          <div className="text-center max-w-xl mx-auto">
            <div className="font-script text-2xl text-leaf">who it's for</div>
            <h2 className="font-display text-3xl sm:text-4xl text-primary font-medium leading-tight mt-1">
              Built for four kinds of<br/><span className="italic font-light text-leaf">campus foodservice.</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
            {ORG_TYPES.map(({ Icon, label }) => (
              <div key={label} className="flex items-center gap-3 p-5 rounded-2xl bg-card border border-border/70 hover:shadow-card transition-shadow">
                <div className="h-11 w-11 shrink-0 rounded-xl bg-secondary text-leaf grid place-items-center">
                  <Icon className="h-5 w-5" />
                </div>
                <div className="font-display text-base text-primary font-medium leading-snug">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CASE-STUDY STRIP — removed until we have measured results */}

      {/* CTA BAND */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto">
          <div className="relative rounded-[2rem] gradient-leaf overflow-hidden shadow-pop">
            <div className="absolute inset-0 bg-noise opacity-[0.04]" />
            <div className="absolute -top-20 -right-20 h-72 w-72 rounded-full bg-accent/30 blur-3xl" />

            <div className="relative grid lg:grid-cols-12 gap-8 p-8 sm:p-12 lg:p-14 items-center text-primary-foreground">
              <div className="lg:col-span-4 hidden lg:block">
                <div className="rounded-3xl overflow-hidden aspect-[4/5]">
                  <img src={CROISSANT_IMG} alt="Tray of croissants" className="w-full h-full object-cover" />
                </div>
              </div>
              <div className="lg:col-span-8">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary-foreground/10 text-xs uppercase tracking-[0.18em]">
                  <Wrench className="h-3.5 w-3.5 text-accent" /> Turnkey onboarding
                </div>
                <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl font-medium mt-4 leading-tight text-balance">
                  Ready to see the numbers on your<br/>
                  <span className="italic font-light text-accent">next semester's tray?</span>
                </h3>
                <p className="mt-4 text-primary-foreground/85 max-w-xl">
                  Book a 20-minute discovery call and we'll ship a free 8-SKU tasting box within the week.
                </p>
                <div className="mt-7 flex flex-wrap gap-3">
                  <Button asChild className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90 px-6 h-12">
                    <Link to="/wholesale#request">Request campus pricing</Link>
                  </Button>
                  <Button asChild variant="outline" className="rounded-full border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 h-12 px-6 bg-transparent">
                    <a href={`mailto:${BRAND.email}?subject=Campus program inquiry`}>Email the campus team</a>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
